import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { CONSENT_METHODS, E164, type ConsentMethod } from "@/contracts";

import { createMessagingContactMutation } from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { OrganizationFilter } from "@/features/organizations/organization-filter";
import { useApiClient } from "@/lib/api-context";
import { CONSENT_METHOD_LABELS } from "./consent-labels";

/** Strips the punctuation people type, matching the contract's transform. */
function normalize(input: string): string {
  return input.replace(/[\s()\-.]/g, "");
}

/** Today in the local timezone, for an `<input type="date">` value. */
function today(): string {
  const now = new Date();

  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

interface AddContactDialogProps {
  onClose: () => void;
}

/**
 * Adding a number, which is really recording a claim that consent exists.
 *
 * The consent fields are required and the date does not default to today.
 * That is deliberate friction: WhatsApp's policy turns on opt-in, and a form
 * that filled this in for you would let somebody add a number without ever
 * considering whether permission was obtained.
 */
export function AddContactDialog({ onClose }: AddContactDialogProps) {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const [phone, setPhone] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("");
  const [whatsappOn, setWhatsappOn] = useState(false);
  const [consentMethod, setConsentMethod] = useState<ConsentMethod | "">("");
  const [consentedAt, setConsentedAt] = useState("");
  const [consentNote, setConsentNote] = useState("");
  const [voiceOn, setVoiceOn] = useState(false);
  const [voiceConsentMethod, setVoiceConsentMethod] = useState<ConsentMethod | "">("");
  const [voiceConsentedAt, setVoiceConsentedAt] = useState("");
  const [voiceConsentNote, setVoiceConsentNote] = useState("");
  const [organizationId, setOrganizationId] = useState("");

  const normalized = normalize(phone);
  const phoneInvalid = phone.trim().length > 0 && !E164.test(normalized);
  const future = whatsappOn && consentedAt.length > 0 && consentedAt > today();
  const voiceFuture = voiceOn && voiceConsentedAt.length > 0 && voiceConsentedAt > today();
  const whatsappReady = whatsappOn && consentMethod !== "" && consentedAt.length > 0 && !future;
  const voiceReady =
    voiceOn && voiceConsentMethod !== "" && voiceConsentedAt.length > 0 && !voiceFuture;

  const blocked =
    normalized.length === 0 ||
    phoneInvalid ||
    displayName.trim().length < 2 ||
    (!whatsappOn && !voiceOn) ||
    (whatsappOn && !whatsappReady) ||
    (voiceOn && !voiceReady) ||
    (isSuperAdmin && organizationId === "");

  const mutation = useMutation({
    ...createMessagingContactMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["messaging-contacts"] });
    },
  });

  async function submit() {
    // `blocked` already covers the empty consent method, and the compiler
    // knows it: narrowing through the alias is what lets `consentMethod` be
    // passed below without a cast.
    if (blocked) {
      return;
    }

    const whatsappConsent = whatsappOn && consentMethod !== "" ? consentMethod : null;
    const callConsent = voiceOn && voiceConsentMethod !== "" ? voiceConsentMethod : null;

    await mutation.mutateAsync({
      phone: normalized,
      displayName: displayName.trim(),
      ...(role.trim().length > 0 ? { role: role.trim() } : {}),
      ...(whatsappConsent === null
        ? {}
        : {
            consentMethod: whatsappConsent,
            // Midday rather than midnight: a date with no time is ambiguous across
            // timezones, and the middle of the day cannot land on the wrong one.
            consentedAt: new Date(`${consentedAt}T12:00:00`).toISOString(),
            ...(consentNote.trim().length > 0 ? { consentNote: consentNote.trim() } : {}),
          }),
      ...(callConsent === null
        ? {}
        : {
            voiceConsentMethod: callConsent,
            voiceConsentedAt: new Date(`${voiceConsentedAt}T12:00:00`).toISOString(),
            ...(voiceConsentNote.trim().length > 0
              ? { voiceConsentNote: voiceConsentNote.trim() }
              : {}),
          }),
      ...(isSuperAdmin ? { organizationId } : {}),
    });

    onClose();
  }

  return (
    <FormDialog
      open
      title={t("dialogAddContactTitle")}
      description={t("dialogAddContactDescription")}
      submitLabel={t("addContact")}
      pending={mutation.isPending}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        {isSuperAdmin ? (
          <OrganizationFilter
            label={tCommon("organization")}
            allLabel={t("selectOrganization")}
            value={organizationId}
            onChange={setOrganizationId}
          />
        ) : null}

        <TextField
          label={t("fieldPhone")}
          hint={t("phoneHint")}
          required
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          {...(phoneInvalid
            ? { error: t("phoneError") }
            : {})}
        />

        <TextField
          label={tCommon("name")}
          required
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />

        <TextField
          label={tCommon("role")}
          hint={t("contactRoleHint")}
          value={role}
          onChange={(event) => setRole(event.target.value)}
        />

        <ChannelBox
          label={t("whatsappConsent")}
          hint={t("whatsappConsentHint")}
          checked={whatsappOn}
          onChange={(on) => {
            setWhatsappOn(on);

            if (!on) {
              setConsentMethod("");
              setConsentedAt("");
              setConsentNote("");
            }
          }}
        />

        {whatsappOn ? (
          <>
            <SelectField
              label={t("fieldHowConsent")}
              required
              value={consentMethod}
              onChange={(value) => setConsentMethod(value as ConsentMethod | "")}
              data={[
                { value: "", label: t("selectOne") },
                ...CONSENT_METHODS.map((method) => ({
                  value: method,
                  label: CONSENT_METHOD_LABELS[method],
                })),
              ]}
            />

            <TextField
              label={t("fieldConsentDate")}
              type="date"
              required
              max={today()}
              value={consentedAt}
              onChange={(event) => setConsentedAt(event.target.value)}
              {...(future ? { error: t("consentFutureError") } : {})}
            />

            <TextAreaField
              label={t("fieldNote")}
              hint={t("hintConsentEvidence")}
              rows={2}
              value={consentNote}
              onChange={(event) => setConsentNote(event.target.value)}
            />
          </>
        ) : null}

        <ChannelBox
          label={t("voiceConsent")}
          hint={t("voiceConsentHint")}
          checked={voiceOn}
          onChange={(on) => {
            setVoiceOn(on);

            if (!on) {
              setVoiceConsentMethod("");
              setVoiceConsentedAt("");
              setVoiceConsentNote("");
            }
          }}
        />

        {voiceOn ? (
          <>
            <SelectField
              label={t("fieldHowConsentVoice")}
              required
              value={voiceConsentMethod}
              onChange={(value) => setVoiceConsentMethod(value as ConsentMethod | "")}
              data={[
                { value: "", label: t("selectOne") },
                ...CONSENT_METHODS.map((method) => ({
                  value: method,
                  label: CONSENT_METHOD_LABELS[method],
                })),
              ]}
            />

            <TextField
              label={t("fieldConsentDateVoice")}
              type="date"
              required
              max={today()}
              value={voiceConsentedAt}
              onChange={(event) => setVoiceConsentedAt(event.target.value)}
              {...(voiceFuture ? { error: t("consentFutureError") } : {})}
            />

            <TextAreaField
              label={t("fieldNote")}
              hint={t("hintConsentEvidence")}
              rows={2}
              value={voiceConsentNote}
              onChange={(event) => setVoiceConsentNote(event.target.value)}
            />
          </>
        ) : null}

        {!whatsappOn && !voiceOn ? (
          <Alert tone="warn" title={t("contactChannelRequiredTitle")} announce={false}>
            {t("contactChannelRequired")}
          </Alert>
        ) : null}

        <Alert tone="warn" title={t("dialogAddContactWarnTitle")} announce={false}>
          {t("dialogAddContactWarnBody")}
        </Alert>
      </div>
    </FormDialog>
  );
}

function ChannelBox({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = useId();

  return (
    <div className="flex items-start gap-2">
      <input
        id={id}
        type="checkbox"
        className="mt-1 size-4 rounded border-border-subtle"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label htmlFor={id} className="text-sm text-ink">
        <span className="font-medium">{label}</span>
        <span className="mt-0.5 block text-xs text-ink-muted">{hint}</span>
      </label>
    </div>
  );
}
